<template>
    <div class="asset-cell" @mouseenter="hovered = true" @mouseleave="hovered = false">
        <template v-if="asset">
            <div v-if="isImage" class="image-preview">
                <viewer :images="[assetUrl]">
                    <div class="thumbnail-wrapper" @click="showImagePreview">
                        <img :src="assetUrl" :alt="$t('table.assetImage')" class="thumbnail" />
                        <md-icon v-if="hovered" class="preview-icon">visibility</md-icon>
                    </div>
                </viewer>
            </div>
            <div v-else-if="isVideo" class="video-preview" @click="showVideoModal">
                <div class="video-thumbnail">
                    <md-icon class="play-icon">play_circle_outline</md-icon>
                    <div class="watch-video-link">{{ $t('table.watchVideo') }}</div>
                </div>
            </div>
            <div v-else-if="isAudio" class="audio-preview">
                <div @click="toggleAudio">
                    <md-icon>{{ paused ? "play_circle" : "pause_circle" }}</md-icon>
                </div>
                <av-waveform
                    :audio-src="assetUrl"
                    :playtime="false"
                    :playtimeWithMs="false"
                    :playtimeTextBottom="false"
                    :playtimeSliderWidth="3"
                    :noplayedLineColor="'#ff671b'"
                    :playedLineColor="'#1890ff'"
                    :playedLineWidth="0.3"
                    :noplayedLineWidth="0.3"
                    :playtimeFontSize="0"
                    :canvWidth="200"
                    :canvHeight="40"
                    :playtimeSliderColor="'#1890ff'"
                ></av-waveform>
            </div>
            <div v-else class="file-preview">
                <div class="file-icon">
                    <md-icon :style="{ color: randomColor() }" v-if="isWordFile">description</md-icon>
                    <md-icon :style="{ color: randomColor() }" v-else-if="isExcelFile">grid_on</md-icon>
                    <md-icon :style="{ color: randomColor() }" v-else-if="isPdfFile">picture_as_pdf</md-icon>
                    <md-icon :style="{ color: randomColor() }" v-else>insert_drive_file</md-icon>
                </div>
                <a :href="assetUrl" target="_blank" download :style="{ color: randomColor() }">
                    {{ asset.name }}
                </a>
            </div>

            <!-- Hover Overlay'de Aksiyon İkonları -->
            <div v-if="hovered && !readonly" class="overlay-actions">
                <PermissionController :permissions="['table.update']">
                    <a-button
                        icon="edit"
                        size="small"
                        @click="enableEdit"
                        style="margin-right: 4px"
                        v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                        content="Değiştir"
                    ></a-button>
                </PermissionController>
                <PermissionController :permissions="['table.update']">
                    <a-button
                        icon="delete"
                        type="danger"
                        size="small"
                        @click="deleteAsset"
                        v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                        content="Sil"
                    ></a-button>
                </PermissionController>
            </div>
        </template>

        <!-- Gizli upload bileşeni - her zaman DOM'da olacak -->
        <a-upload
            ref="hiddenUploader"
            :before-upload="beforeUpload"
            :custom-request="uploadFile"
            :show-upload-list="false"
            :accept="acceptedFileTypes"
            style="display: none"
        >
            <div></div>
        </a-upload>

        <!-- Görünür upload bileşeni - sadece asset yokken görünür -->
        <PermissionController :permissions="['table.update']">
            <a-upload
                v-if="!asset"
                :before-upload="beforeUpload"
                :custom-request="uploadFile"
                :show-upload-list="false"
                :accept="acceptedFileTypes"
            >
                <div class="upload-button">
                    <md-icon>{{ uploadIcon }}</md-icon>
                    {{ uploadText }}
                </div>
            </a-upload>
        </PermissionController>

        <Modal2
            v-if="showModal"
            @cancel="closeModal"
            :show-footer="false"
            :show-title="false"
            className="asset-viewer-modal keydown-prevent-modal"
        >
            <video-player
                class="modal-video-player"
                ref="modalVideoPlayer"
                :options="playerOptions()"
                :playsinline="true"
            ></video-player>
        </Modal2>
    </div>
</template>

<script>
import Vue from "vue";
import { uploadAsset } from "../../../../../api/assetUpload";
import axios from "axios";
import "video.js/dist/video-js.css";
import { videoPlayer } from "vue-video-player";
import "viewerjs/dist/viewer.css";
import Viewer from "v-viewer";
import Modal2 from "../../../../../components/common/Modal2.vue";

Vue.use(Viewer);
export default Vue.extend({
    name: "AssetCell",
    components: {
        Modal2,
        videoPlayer,
    },
    props: {
        record: {
            type: Object,
            required: true,
        },
        field: {
            type: Object,
            required: true,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            editing: false,
            showModal: false,
            hovered: false,
            paused: true,
        };
    },
    computed: {
        asset() {
            return this.record.data[this.field._id];
        },
        assetUrl() {
            if (!this.asset) return "";
            return this.asset.cdnUrl
                ? this.asset.cdnUrl
                : `https://static.supsis.live/${this.asset.path}${this.asset._id}.${this.asset.ext}`;
        },
        acceptedFileTypes() {
            const fileTypes = {
                file: "*",
                video: "video/*",
                image: "image/*",
                audio: "audio/*",
            };
            return fileTypes[this.field.fieldType] || "*";
        },
        isImage() {
            return this.asset && this.asset.mime.startsWith("image");
        },
        isVideo() {
            return this.asset && this.asset.mime.startsWith("video");
        },
        isAudio() {
            return this.asset && this.asset.mime.startsWith("audio");
        },
        isWordFile() {
            return this.asset && (this.asset.ext === "doc" || this.asset.ext === "docx");
        },
        isExcelFile() {
            return this.asset && (this.asset.ext === "xls" || this.asset.ext === "xlsx");
        },
        isPdfFile() {
            return this.asset && this.asset.ext === "pdf";
        },
        uploadText() {
            const fileTypeText = {
                file: this.$t("tables.file"),
                video: this.$t("tables.video"),
                image: this.$t("tables.image"),
                audio: this.$t("tables.audio"),
            };
            return `${fileTypeText[this.field.fieldType] || this.$t("tables.file")} ${this.$t("tables.upload")}`;
        },
        uploadIcon() {
            const fileTypeIcon = {
                file: "insert_drive_file",
                video: "video_library",
                image: "image",
                audio: "audiotrack",
            };
            return fileTypeIcon[this.field.fieldType] || "insert_drive_file";
        },
    },
    methods: {
        randomColor() {
            const colors = ["#FF6B6B", "#4ECDC4", "#1A535C", "#FF7F50", "#6495ED", "#9FE2BF"];
            return colors[Math.floor(Math.random() * colors.length)];
        },
        enableEdit() {
            if (this.readonly) {
                this.$message.error("Seçim modu içinde bu alanı düzenleyemezsiniz");
                return;
            }

            // Gizli upload bileşeninin input elementini programatik olarak tıkla
            this.$nextTick(() => {
                if (this.$refs.hiddenUploader) {
                    const uploadInput = this.$refs.hiddenUploader.$el.querySelector('input[type="file"]');
                    if (uploadInput) {
                        uploadInput.click();
                    } else {
                        this.$message.error("Dosya seçici açılamadı");
                    }
                }
            });
        },
        deleteAsset() {
            if (this.readonly) {
                this.$message.error("Seçim modu içinde bu alanı düzenleyemezsiniz");
                return;
            }
            try {
                this.$emit("update-record", {
                    recordId: this.record._id,
                    fieldId: this.field._id,
                    value: null,
                });
                this.$message.success("Dosya başarıyla silindi");
            } catch (error) {
                console.error("Asset silme hatası:", error);
                this.$message.error("Dosya silinirken bir hata oluştu");
            }
        },
        beforeUpload(file) {
            const isValidType = file.type.indexOf(this.field.fieldType) !== -1 || this.field.fieldType === "file";
            if (!isValidType) {
                this.$message.error("Geçersiz dosya türü");
            }
            return isValidType;
        },
        async uploadFile({ file }) {
            const siteId = this.$store.state.site.site._id;
            const path = `${siteId}/table-assets/`;
            try {
                const uploadData = await uploadAsset({
                    path,
                    name: file.name,
                    size: file.size,
                    type: "table-asset",
                    mime: file.type,
                    ext: file.name.split(".").pop(),
                });
                await axios({
                    method: uploadData.data.method,
                    url: uploadData.data.url,
                    data: file,
                    headers: { "Content-Type": "application/octet-stream" },
                });
                await this.updateRecord(uploadData.data.asset);
                this.$message.success("Yükleme başarılı");
            } catch (error) {
                console.error("Asset upload error:", error);
                this.$message.error("Yükleme hatası");
            } finally {
                this.editing = false;
            }
        },
        async updateRecord(asset) {
            if (this.readonly) {
                this.$message.error("Seçim modu içinde bu alanı düzenleyemezsiniz");
                return;
            }
            try {
                await this.$emit("update-record", {
                    recordId: this.record._id,
                    fieldId: this.field._id,
                    value: asset,
                });
            } catch (error) {
                console.error("Record update error:", error);
                this.$message.error("Güncelleme hatası");
            }
        },
        showImagePreview() {
            const imageElement = this.$el.querySelector(".thumbnail");
            imageElement.click();
        },
        showVideoModal() {
            this.showModal = true;
        },
        closeModal() {
            this.showModal = false;
        },
        playerOptions() {
            const mime = this.asset.mime;
            return {
                muted: false,
                language: "tr",
                playbackRates: [0.7, 1.0, 1.5, 2.0],
                sources: [
                    {
                        type: mime,
                        src: this.assetUrl,
                    },
                ],
                poster: undefined,
                width: 600,
                height: 300,
            };
        },
        toggleAudio() {
            const audio = this.$el.querySelector("audio");
            audio[audio.paused ? "play" : "pause"]();
            this.paused = !this.paused;
        },
    },
});
</script>

<style scoped lang="scss">
.asset-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    position: relative;
    overflow: hidden;
    .existing-asset {
        a {
            text-decoration: underline;
        }
    }

    .upload-button {
        display: flex;
        align-items: center;
        cursor: pointer;
        color: #1890ff;

        .md-icon {
            margin-right: 4px;
        }
    }

    .image-preview {
        position: relative;
        cursor: pointer;

        .thumbnail-wrapper {
            position: relative;
            cursor: pointer;

            .thumbnail {
                max-width: 40px;
                max-height: 40px;
                object-fit: cover;
            }

            .preview-icon {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                color: white;
                font-size: 24px;
                opacity: 0;
                transition: opacity 0.3s;
            }

            &:hover .preview-icon {
                opacity: 1;
            }
        }
    }

    .video-preview {
        cursor: pointer;

        .video-thumbnail {
            display: flex;
            align-items: center;
            gap: 10px;

            .play-icon {
                font-size: 48px;
                color: #1890ff;
            }

            .watch-video-link {
                color: #1890ff;
                text-decoration: underline;
            }
        }
    }

    .audio-preview {
        display: flex;
        align-items: center;
        max-width: 300px;

        .md-icon {
            margin-right: 8px;
            cursor: pointer;
        }
    }

    .file-preview {
        display: flex;
        align-items: center;

        .file-icon {
            margin-right: 8px;
        }

        a {
            text-decoration: underline;
        }
    }

    .overlay-actions {
        position: absolute;
        top: 2px;
        right: 2px;
        display: flex;
        gap: 4px;
        z-index: 10;
    }
}
</style>

<style lang="scss">
.asset-viewer-modal {
    .ant-modal {
        width: 800px !important;

        .modal-video-player {
            width: 100%;
            height: 100%;
        }
    }
}
</style>
